import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Clock,
  CircleDollarSign,
  Accessibility,
  Star,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowLeft,
  Share2,
  Heart,
  Navigation,
  Loader2,
  Info,
} from "lucide-react";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useAttraction } from "../../../services/attractionService";
import { Button } from "../../../components/ui/button";

const AttractionDetail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const isDark = useSelector((state: RootState) => state.theme.darkMode);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );

  // Fetch attraction from API
  const {
    data: attraction,
    isLoading,
    error,
  } = useAttraction(id ? parseInt(id) : 0, !!id);

  const images = useMemo(() => {
    if (!attraction) return [];
    const galleryItems = attraction.images || attraction.gallery || [];
    if (galleryItems.length > 0) return galleryItems;
    return attraction.image ? [attraction.image] : [];
  }, [attraction]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
            <p className="text-gray-500 animate-pulse">{t("common.loading", "Loading paradise...")}</p>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !attraction) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl p-12 text-center max-w-xl mx-auto border border-gray-100 dark:border-gray-700">
            <div className="bg-red-50 dark:bg-red-900/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Info className="h-10 w-10 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {error
                ? t("attractions.errorLoading", "Oops! Something went wrong")
                : t("attractions.notFound", "Attraction Not Found")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg">
              {error
                ? t(
                  "attractions.errorLoadingText",
                  "We couldn't reach the destination. Please try again later."
                )
                : t(
                  "attractions.notFoundText",
                  "The hidden gem you're looking for seems to be very well hidden."
                )}
            </p>
            <Button
              onClick={() => navigate(`/${currentLanguage}/attractions`)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all shadow-lg hover:shadow-emerald-500/20"
            >
              {t("attractions.backToAttractions", "Back to Attractions")}
            </Button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  // Mock reviews data
  const reviews = [
    {
      user: "Ethan Bennett",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100",
      rating: 4,
      timeAgo: "3 months ago",
      comment: "A beautiful and peaceful place to relax and enjoy nature. Well-maintained and offers a refreshing escape.",
      likes: 8,
    },
  ];

  const nextImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (selectedImageIndex !== null) {
      setSelectedImageIndex((selectedImageIndex - 1 + images.length) % images.length);
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-white"}`}>
      <Header />

      <main>
        {/* Immersive Hero Section */}
        <div className="relative h-[65vh] w-full overflow-hidden">
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 10, ease: "linear" }}
            className="absolute inset-0"
          >
            <img
              src={attraction.image || images[0]}
              alt={attraction.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          </motion.div>

          {/* Hero Content */}
          <div className="absolute inset-0 flex flex-col justify-end pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl"
              >
                <nav className="flex items-center gap-2 text-white/80 text-sm mb-4">
                  <button
                    onClick={() => navigate(`/${currentLanguage}/attractions`)}
                    className="hover:text-white transition-colors flex items-center gap-1"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t("attractions.back", "Directories")}
                  </button>
                  <span>•</span>
                  <span className="text-emerald-400 font-medium">{attraction.category || "Sightseeing"}</span>
                </nav>
                <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                  {attraction.title}
                </h1>

                <div className="flex flex-wrap items-center gap-6 text-white/90">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-400" />
                    <span className="font-medium">{attraction.location || "Central Region"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < 4 ? "text-yellow-400 fill-yellow-400" : "text-gray-400"}`} />
                    ))}
                    <span className="ml-2 font-medium">(4.8 • 120 reviews)</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="absolute top-8 right-8 flex gap-3">
            <button className="h-12 w-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all">
              <Heart className="h-5 w-5" />
            </button>
            <button className="h-12 w-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all">
              <Share2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content Layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-12">

              {/* About Section */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="flex items-center gap-2">
                  <div className="h-8 w-1 bg-emerald-600 rounded-full" />
                  <h2 className="text-3xl font-bold dark:text-white">{t("attractions.theExperience", "The Experience")}</h2>
                </div>
                <div className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 leading-relaxed font-light">
                  {attraction.about || attraction.description}
                </div>
              </motion.section>

              {/* Gallery Section */}
              {images.length > 1 && (
                <motion.section
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-3xl font-bold dark:text-white">{t("attractions.gallery", "Visual Journey")}</h2>
                    <span className="text-gray-500">{images.length} {t("attractions.photos", "Photos")}</span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 auto-rows-[200px]">
                    {images.slice(0, 6).map((image, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`group relative rounded-2xl overflow-hidden cursor-pointer ${idx === 0 ? "md:col-span-2 md:row-span-2" : ""
                          }`}
                      >
                        <img
                          src={image}
                          alt={`Gallery ${idx}`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Reviews Section */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-8"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-3xl font-bold dark:text-white">{t("attractions.visitorStories", "Visitor Stories")}</h2>
                  <Button variant="outline" className="rounded-full">{t("attractions.writeReview", "Share your experience")}</Button>
                </div>

                <div className="grid gap-6">
                  {reviews.map((review, idx) => (
                    <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 p-8 rounded-3xl border border-gray-100 dark:border-gray-700">
                      <div className="flex items-center gap-4 mb-4">
                        <img src={review.avatar} alt={review.user} className="h-12 w-12 rounded-full object-cover" />
                        <div>
                          <h4 className="font-bold dark:text-white">{review.user}</h4>
                          <p className="text-sm text-gray-500">{review.timeAgo}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed italic">"{review.comment}"</p>
                    </div>
                  ))}
                </div>
              </motion.section>
            </div>

            {/* Sticky Sidebar */}
            <div className="space-y-8">
              <div className="sticky top-24 space-y-8">

                {/* Practical Info Card */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white dark:bg-gray-800 shadow-xl rounded-[2rem] p-8 border border-gray-100 dark:border-gray-700 overflow-hidden relative"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 blur-3xl" />

                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2 dark:text-white">
                    <Info className="h-5 w-5 text-emerald-600" />
                    {t("attractions.planYourVisit", "Plan Your Visit")}
                  </h3>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl">
                        <Clock className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{t("attractions.openingHours", "Hours")}</p>
                        <p className="text-gray-900 dark:text-gray-200 font-medium">
                          {attraction.practicalInfo?.openingHours || "08:00 AM - 06:00 PM"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl">
                        <CircleDollarSign className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{t("attractions.entranceFee", "Access Fee")}</p>
                        <p className="text-gray-900 dark:text-gray-200 font-medium">
                          {attraction.practicalInfo?.entranceFee || "Free Entry"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl">
                        <Accessibility className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">{t("attractions.accessibility", "Accessibility")}</p>
                        <p className="text-gray-900 dark:text-gray-200 font-medium line-clamp-2">
                          {attraction.practicalInfo?.accessibility || "Partially accessible"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button className="w-full mt-8 py-6 bg-emerald-600 hover:bg-emerald-700 rounded-2xl text-lg font-bold shadow-lg shadow-emerald-500/20 group">
                    {t("attractions.bookTicket", "Check Availability")}
                    <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </motion.div>

                {/* Map Card */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white dark:bg-gray-800 shadow-xl rounded-[2rem] overflow-hidden border border-gray-100 dark:border-gray-700"
                >
                  <div className="h-48 bg-gray-200 relative group cursor-pointer">
                    {attraction.locationImage ? (
                      <img src={attraction.locationImage} alt="Location" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-emerald-50 dark:bg-gray-700">
                        <Navigation className="h-12 w-12 text-emerald-200 dark:text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button className="bg-white text-emerald-900 hover:bg-white/90 font-bold px-6 py-2 rounded-full shadow-2xl">
                        {t("attractions.openMaps", "View Map")}
                      </Button>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 dark:text-gray-400 text-sm flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-emerald-600" />
                      {attraction.location}
                    </p>
                  </div>
                </motion.div>

                {/* Nearby Teaser */}
                {attraction.nearbyAttractions && attraction.nearbyAttractions.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-bold text-gray-900 dark:text-white px-2 uppercase tracking-widest text-xs">
                      {t("attractions.exploreNearby", "Explore Nearby")}
                    </h4>
                    <div className="space-y-3">
                      {attraction.nearbyAttractions.slice(0, 2).map((nearby, i) => (
                        <div key={i} className="flex gap-4 p-3 rounded-2xl hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-colors cursor-pointer group">
                          <img src={nearby.image} className="h-16 w-16 rounded-xl object-cover" />
                          <div className="flex-1 min-w-0">
                            <h5 className="font-bold text-sm dark:text-white truncate group-hover:text-emerald-600 transition-colors">{nearby.title}</h5>
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3" />
                              15 mins away
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-[999] flex items-center justify-center p-4"
            onClick={() => setSelectedImageIndex(null)}
          >
            <Button
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-8 right-8 text-white h-12 w-12 rounded-full bg-white/10 hover:bg-white/20"
            >
              <X className="h-6 w-6" />
            </Button>

            <div className="w-full max-w-6xl aspect-video relative flex items-center justify-center" onClick={e => e.stopPropagation()}>
              <motion.img
                key={selectedImageIndex}
                src={images[selectedImageIndex]}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-h-full max-w-full object-contain shadow-2xl"
              />

              <button
                onClick={prevImage}
                className="absolute left-4 p-4 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all border border-white/20"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>

              <button
                onClick={nextImage}
                className="absolute right-4 p-4 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all border border-white/20"
              >
                <ChevronRight className="h-8 w-8" />
              </button>

              <div className="absolute -bottom-16 text-white text-lg font-light tracking-widest uppercase">
                {selectedImageIndex + 1} / {images.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AttractionDetail;
